import { X } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [contenteditable="true"], [tabindex]:not([tabindex="-1"])';

/**
 * The edit form used to render at the top of the page above a long list, so
 * clicking Edit on row 10 opened something the operator could not see. A
 * slide-over keeps the form next to the row it belongs to.
 */
export const FormDrawer = ({ open, title, description, onClose, footer, children }) => {
  const { t } = useTranslation();
  const panelRef = useRef(null);
  const previouslyFocused = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    previouslyFocused.current = document.activeElement;
    document.body.classList.add('admin-scroll-lock');

    const frame = window.requestAnimationFrame(() => {
      const first = panelRef.current?.querySelector(FOCUSABLE);
      (first || panelRef.current)?.focus();
    });

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;

      const nodes = Array.from(panelRef.current?.querySelectorAll(FOCUSABLE) || []);
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.classList.remove('admin-scroll-lock');
      window.cancelAnimationFrame(frame);
      if (previouslyFocused.current instanceof HTMLElement) previouslyFocused.current.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-beam-ink/60 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="form-drawer-title"
        tabIndex={-1}
        className="absolute inset-y-0 right-0 flex w-full max-w-3xl flex-col bg-white shadow-crisp outline-none"
      >
        <header className="flex items-start justify-between gap-4 border-b border-beam-blue/10 p-5">
          <div className="min-w-0">
            <h2 id="form-drawer-title" className="font-display text-2xl font-extrabold">
              {title}
            </h2>
            {description ? (
              <p className="mt-1 text-sm font-semibold text-beam-graphite/60">{description}</p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="focus-ring grid h-10 w-10 shrink-0 place-items-center rounded-lg text-beam-graphite hover:bg-beam-light hover:text-beam-ink"
            aria-label={t('common.close')}
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto p-5">{children}</div>

        {footer ? (
          <footer className="flex flex-wrap items-center gap-2 border-t border-beam-blue/10 bg-beam-light/40 p-4">
            {footer}
          </footer>
        ) : null}
      </div>
    </div>
  );
};
